import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, Inject, OnInit, signal, computed, DestroyRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  finalize,
  forkJoin,
  Observable,
  of,
  tap,
  switchMap,
  concatMap,
} from 'rxjs';
import { AuthenticationMethod, IntegrationModel, IntegrationToAddModel, Platform } from 'src/app/models/api';
import {
  IntegrationCreationResponseModel,
  IntegrationCredentials,
  StatusCodeEnum,
} from 'src/app/models/api/integration.model';
import {
  IIntegrationComponentInput,
  IIntegrationComponentOutput,
} from 'src/app/models/componentInputs/integrationComponentInput.interface';
import { CredentialKey, SecretCredentialsKeys } from 'src/app/models/constants/credentials.constants';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { ISelectOption, PlatformSelectOptions } from 'src/app/models/selectOptions';
import { IDetailKeyValue } from 'src/app/models/singlePageComponents';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { RequiredFieldTitlePipe } from 'src/app/pipes/required-field-title/required-field-title.pipe';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { IntegrationsHttpService } from 'src/app/services/httpServices/integrationsHttpsService/integrations-http.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { integrationIsFailing, integrationIsNotFailing } from './utils';
import {
  CredentialValidatorsFunctions,
  GuidCredentialValidatorsFunctions,
  RequiredValidatorFunction,
} from 'src/app/components/integration/integration-credentials/consts';
import { InLineIntegrationsHttpService } from 'src/app/services/httpServices/edgeIntegrationsHttpsService/in-line-integrations-http.service';
import {
  CreateEdgeIntegrationRequest,
  InLineIntegration,
  InLineIntegrationUpdateData,
  FoundryApiKeyResponse,
  Provider,
} from 'src/app/models/api/inLineIntegration/inLineIntegration.model';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { UserService } from 'src/app/services/infra/user/user.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  DevicesEnrollmentKeyHttpService,
  EnrollmentKeyResponse,
} from 'src/app/services/httpServices/devicesEnrollmentKeyHttpService/devices-enrollment-key-http.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-integration',
  templateUrl: './integration.component.html',
  styleUrls: ['./integration.component.scss'],
})
export class IntegrationComponent implements OnInit {
  public integrationWasUpdated = false;
  public inlineWebhookWasUpdated = false;
  public showNoData = false;
  public formSubmitAttempt = false; // using non-primitive boolean to pass by ref
  public shouldUpdateCredentials = false;
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public parentIsLoading: BehaviorSubject<boolean>;
  public authMethodHasChanged = false;

  public integrationStatusCodeEnum = StatusCodeEnum;
  public authenticationMethod = AuthenticationMethod;
  public isInlinePreventionEnabled = false;
  public shouldShowInlineControlSection = signal(false);
  public shouldShowCreateModeInlinePreview = signal(false);

  // Copilot Studio webhook exists (loaded from API)
  public copilotStudioEnabled = signal(false);

  // Foundry webhook exists (loaded from API)
  public foundryEnabled = signal(false);
  public foundryApplicationId = signal('');
  public foundryApiKeyExists = signal(false);
  public foundryApiKeyVisible = signal(false);
  public foundryApiKeyValue = signal('');
  public foundryApiKeyCopied = signal(false);
  public foundryIntegration?: InLineIntegration;
  public foundryIntegrationId?: string;

  // Main block is open when user toggles it on or any webhook exists
  private _mainToggleExpanded = signal(false);
  public inlineWebhookExpanded = computed(
    () => this.copilotStudioEnabled() || this.foundryEnabled() || this._mainToggleExpanded()
  );

  // Empty integration Id is equal to in Add mode.
  public integrationId?: string;
  public isInAddMode: boolean;
  public integration: IntegrationModel = new IntegrationModel();
  public integrationCredentials: IntegrationCredentials;
  public inLineIntegration?: InLineIntegration;
  public inLineIntegrationId?: string;
  public inLineIntegrationApplicationId?: string;
  public actionsOptions: ISelectOption[] = [];
  public integrationNameDetail: IDetailKeyValue;

  public devicesEnrollmentKey?: string;

  public isDevicesIntegrationCreatedWithKey(): boolean {
    const platform = this.integrationFormGroup?.controls?.platform?.value;
    return (
      (platform === Platform.devices || platform === Platform.cursor) &&
      !!this.devicesEnrollmentKey &&
      this.isInAddMode
    );
  }

  public credentialKey = CredentialKey;
  public _inLineApplicationIdValidators = [...GuidCredentialValidatorsFunctions, Validators.required];

  public integrationFormGroup: FormGroup<{
    name: FormControl<string>;
    platform: FormControl<string>;
    environment: FormControl<string>;
    authMethod: FormControl<string>;
    credentials: UntypedFormGroup;
  }>;

  public inLineIntegrationFormGroup: FormGroup<{
    inLineServiceEnabled: FormControl<boolean>;
    inLineServiceApplicationId: FormControl<string>;
  }>;

  public foundryFormGroup: FormGroup<{
    foundryEnabled: FormControl<boolean>;
  }>;

  public credentialsFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  public inLineServiceCredentialsFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  public errorMessage: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public errorMessageInlineWebhook: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private _integrationInitialName: string;

  constructor(
    public deleteConnectionDialog: MatDialog,
    private _fb: FormBuilder,
    private _labelsProviderPipe: LabelsProviderPipe,
    private _requiredFieldPipe: RequiredFieldTitlePipe,
    public dialogRef: MatDialogRef<IntegrationComponent>,
    @Inject(MAT_DIALOG_DATA) input: IIntegrationComponentInput,
    private _integrationsHttpService: IntegrationsHttpService,
    private _inLineIntegrationsHttpService: InLineIntegrationsHttpService,
    private _devicesEnrollmentKeyHttpService: DevicesEnrollmentKeyHttpService,
    private _httpErrorHandlerService: HttpErrorHandlerService,
    private _featureFlagService: FeatureFlagsService,
    private _loggerService: LoggerService,
    private _notificationService: NotificationsService,
    private _destroyRef: DestroyRef,
    private _userService: UserService
  ) {
    this.integrationId = input.integrationId;
    this.isInAddMode = !input.integrationId;
    this.parentIsLoading = input.parentIsLoading;
    this.isInlinePreventionEnabled = this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.InlinePrevention);

    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => {
      this.closeWindow();
    });

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.Salesforce)) {
      const salesforceOption = PlatformSelectOptions.filter(
        (option) => Platform.salesforce == (option.id as Platform)
      )[0];
      salesforceOption.disabled = true;
    }

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.ServiceNow)) {
      const serviceNowOption = PlatformSelectOptions.filter(
        (option) => Platform.serviceNow == (option.id as Platform)
      )[0];
      serviceNowOption.disabled = true;
    }

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.ChatGptEnterprise)) {
      const chatGptEnterpriseOption = PlatformSelectOptions.filter(
        (option) => Platform.chatGptEnterprise == (option.id as Platform)
      )[0];
      chatGptEnterpriseOption.disabled = true;
    }

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.AmazonBedrock)) {
      const amazonBedrockOption = PlatformSelectOptions.filter(
        (option) => Platform.amazonBedrock == (option.id as Platform)
      )[0];
      amazonBedrockOption.disabled = true;
    }

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.Google)) {
      const googleOption = PlatformSelectOptions.filter((option) => Platform.google == (option.id as Platform))[0];
      googleOption.disabled = true;
    }

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.Devices)) {
      const devicesOption = PlatformSelectOptions.filter((option) => Platform.devices == (option.id as Platform))[0];
      devicesOption.disabled = true;
    }

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.Cursor)) {
      const cursorOption = PlatformSelectOptions.filter((option) => Platform.cursor == (option.id as Platform))[0];
      cursorOption.disabled = true;
    }

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.Budibase)) {
      const budibaseOption = PlatformSelectOptions.filter((option) => Platform.budibase == (option.id as Platform))[0];
      budibaseOption.disabled = true;
    }

    if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.Airtable)) {
      const airtableOption = PlatformSelectOptions.filter((option) => Platform.airtable == (option.id as Platform))[0];
      airtableOption.disabled = true;
    }
  }

  public ngOnInit(): void {
    this._initSinglePage();
  }

  public onAddIntegration() {
    const input: IntegrationToAddModel = {
      name: this.integrationFormGroup.controls.name.value,
      description: '',
      platform: this.integrationFormGroup.controls.platform.value as Platform,
      authenticationMethod: this.integrationFormGroup.controls.authMethod.value as AuthenticationMethod,
      environment: this.integrationFormGroup.controls.environment.value,
      credentials: this._getFilteredCredentials(),
    };

    if (input.platform !== Platform.devices && input.platform !== Platform.cursor) {
      this.isLoading$.next(true);
    }

    this._integrationsHttpService.addIntegration(input).subscribe({
      next: (response: IntegrationCreationResponseModel) => {
        this.integrationWasUpdated = true;
        this.integrationId = response.integrationId;
        const createdIntegrationId = response.integrationId;

        if (input.platform === Platform.devices || input.platform === Platform.cursor) {
          // Cursor reuses the devices infrastructure on ai-edge-manager
          const edgePlatform = Platform.devices;
          this._createInlineIntegrationForEnrollmentKeyPlatform(createdIntegrationId, edgePlatform)
            .pipe(
              switchMap(() => {
                return this._createEnrollmentKeyForEnrollmentKeyPlatform(edgePlatform, createdIntegrationId);
              }),
              takeUntilDestroyed(this._destroyRef)
            )
            .subscribe();
        } else {
          this._initSinglePage(true);
        }
      },
      error: (error) => {
        this._httpErrorHandlerService.handle(error);
        this.isLoading$.next(false);
      },
    });
  }

  public onCancel() {
    // if canceled on Add mode - nothing to update
    // if in edit mode - there can be some updates
    // such as in case title was updated successfully but integration fail on credentials
    // in that case the pop up keeps open and user can press on cancel
    this.closeWindow(this.integrationId ? this.integrationWasUpdated : false);
  }

  public closeWindow(refreshNeeded = false) {
    const doRefresh =
      (this.integrationId ? this.integrationWasUpdated : false) || this.inlineWebhookWasUpdated || refreshNeeded;
    const output = {
      refreshNeeded: doRefresh,
      integrationId: this.integrationId,
    } as IIntegrationComponentOutput;
    this.dialogRef.close(output);
  }

  public onMainInlineToggleChange(event: { checked: boolean }): void {
    if (event.checked) {
      this._mainToggleExpanded.set(true);
      return;
    }

    const copilotId = this.inLineIntegrationId;
    const foundryId = this.foundryIntegrationId;

    if (!copilotId && !foundryId) {
      this._mainToggleExpanded.set(false);
      return;
    }

    this._openRemoveConfirmationDialog(
      'integrationPage.removeAllInlineWebhooksConfirmation.title',
      'integrationPage.removeAllInlineWebhooksConfirmation.content'
    )
      .pipe(
        concatMap((confirmed: boolean) => {
          if (!confirmed) {
            this.inLineIntegrationFormGroup?.controls?.inLineServiceEnabled?.setValue(true, { emitEvent: false });
            return EMPTY;
          }
          this.isLoading$.next(true);
          const deletes: Observable<unknown>[] = [];
          if (copilotId) {
            deletes.push(this._inLineIntegrationsHttpService.deleteInLineIntegration(copilotId, 'copilotStudio'));
          }
          if (foundryId) {
            deletes.push(this._inLineIntegrationsHttpService.deleteInLineIntegration(foundryId, 'foundry'));
          }
          return forkJoin(deletes).pipe(finalize(() => this.isLoading$.next(false)));
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          if (copilotId) {
            this._onInLineWebhookRemoveSuccess();
          }
          if (foundryId) {
            this._onFoundryWebhookRemoveSuccess();
            this.foundryEnabled.set(false);
          }
          this._mainToggleExpanded.set(false);
        },
        error: (error) => {
          this.inLineIntegrationFormGroup?.controls?.inLineServiceEnabled?.setValue(true, { emitEvent: false });
          if (error instanceof HttpErrorResponse) {
            this._httpErrorHandlerService.handle(error);
          }
          this._notificationService.error('Failed to remove in-line webhooks');
        },
      });
  }

  public onCopilotStudioToggleChange(event: { checked: boolean }): void {
    if (event.checked) {
      this.copilotStudioEnabled.set(true);
      this.inLineIntegrationFormGroup?.controls?.inLineServiceEnabled?.setValue(true, { emitEvent: false });
      return;
    }

    const integrationId = this.inLineIntegrationId;
    if (!integrationId) {
      this.copilotStudioEnabled.set(false);
      return;
    }

    this._openRemoveConfirmationDialog(
      'integrationPage.removeCopilotStudioConfirmation.title',
      'integrationPage.removeCopilotStudioConfirmation.content'
    )
      .pipe(
        concatMap((confirmed: boolean) => {
          if (!confirmed) {
            this.copilotStudioEnabled.set(true);
            return EMPTY;
          }
          this.isLoading$.next(true);
          return this._inLineIntegrationsHttpService
            .deleteInLineIntegration(integrationId, 'copilotStudio')
            .pipe(finalize(() => this.isLoading$.next(false)));
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._onInLineWebhookRemoveSuccess();
        },
        error: (error) => {
          this.copilotStudioEnabled.set(true);
          this._onInLineWebhookRemoveFailure(error);
        },
      });
  }

  public onFoundryToggleChange(event: { checked: boolean }): void {
    if (event.checked) {
      this._createFoundryOnToggle();
      return;
    }

    const integrationId = this.foundryIntegrationId;
    if (!integrationId) {
      this.foundryEnabled.set(false);
      return;
    }

    this._openRemoveConfirmationDialog(
      'integrationPage.removeFoundryConfirmation.title',
      'integrationPage.removeFoundryConfirmation.content'
    )
      .pipe(
        concatMap((confirmed: boolean) => {
          if (!confirmed) {
            this.foundryEnabled.set(true);
            this.foundryFormGroup?.controls?.foundryEnabled?.setValue(true, { emitEvent: false });
            return EMPTY;
          }
          this.isLoading$.next(true);
          return this._inLineIntegrationsHttpService
            .deleteInLineIntegration(integrationId, 'foundry')
            .pipe(finalize(() => this.isLoading$.next(false)));
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._onFoundryWebhookRemoveSuccess();
          this.foundryEnabled.set(false);
        },
        error: (error) => {
          this.foundryEnabled.set(true);
          this.foundryFormGroup?.controls?.foundryEnabled?.setValue(true, { emitEvent: false });
          this._onFoundryWebhookRemoveFailure(error);
        },
      });
  }

  private _createFoundryOnToggle(): void {
    const tenantId =
      this.credentialsFormGroup.get(CredentialKey.tenantId)?.value ||
      this.integrationCredentials?.[CredentialKey.tenantId] ||
      '';
    const relatedIntegrationId = this.integrationId;

    if (!relatedIntegrationId || !tenantId) {
      this._loggerService.error('Foundry toggle ON but missing required data', {
        integrationId: relatedIntegrationId,
        tenantId,
      });
      this.foundryEnabled.set(false);
      this.foundryFormGroup?.controls?.foundryEnabled?.setValue(false, { emitEvent: false });
      return;
    }

    if (this.foundryIntegrationId) {
      this.foundryEnabled.set(true);
      return;
    }

    this.isLoading$.next(true);
    this.foundryEnabled.set(true);

    const updateData: InLineIntegrationUpdateData = {
      platformAccountId: tenantId,
      applicationId: uuidv4(),
    };

    this._createFoundryIntegration(relatedIntegrationId, updateData);
  }

  public isFoundrySaveEnabled(): boolean {
    return this.foundryEnabled() && this.foundryFormGroup?.valid;
  }

  public onUpdate() {
    const shouldUpdateName = this._integrationInitialName !== this.integrationFormGroup.controls.name.value;
    this.shouldUpdateCredentials = this._shouldUpdateCredentials();

    if (!(shouldUpdateName || this.shouldUpdateCredentials)) {
      this.closeWindow(false);
    }

    if (shouldUpdateName) {
      this.isLoading$.next(true);
      this._integrationsHttpService
        .editIntegration(this.integration.id, {
          name: this.integrationFormGroup.controls.name.value,
        })
        .subscribe({
          next: () => {
            this.integrationWasUpdated = true;
            // if credentials are the same - no need to update them
            if (!this.shouldUpdateCredentials) {
              this.closeWindow(true);
            } else {
              this._onEditIntegrationAuthentication();
            }
          },
          error: (error) => {
            this.isLoading$.next(false);
            this.shouldUpdateCredentials = false;
            this.errorMessage.next(this._labelsProviderPipe.transform('integrationPage.errorMessageOnUpdate'));
            this._httpErrorHandlerService.handle(error);
          },
        });
    } else {
      this._onEditIntegrationAuthentication();
    }
  }

  public onSaveInlineIntegration() {
    const tenantId = this.credentialsFormGroup.get(CredentialKey.tenantId)?.value || '';
    const relatedIntegrationId = this.integrationId;

    // In all these cases the inline webhook save button is not visiable so we shouldn get inside this if statement
    if (
      !relatedIntegrationId ||
      !this.inLineIntegrationFormGroup.valid ||
      !this.inLineIntegrationFormGroup.controls.inLineServiceEnabled.value ||
      !tenantId
    ) {
      this._loggerService.error("In-line webhook save button was visible/enabled when it shouldn't be", {
        integrationId: relatedIntegrationId,
        isInLineIntegrationFormGroupValidationValid: this.inLineIntegrationFormGroup.valid,
        isInLineIntegrationSectionEnabled: this.inLineIntegrationFormGroup.controls.inLineServiceEnabled.value,
        tenantId: tenantId,
      });
      return;
    }

    this.isLoading$.next(true);
    if (this.integrationFormGroup.dirty) {
      this.onUpdate();
      return;
    }

    try {
      const updateData: InLineIntegrationUpdateData = {
        platformAccountId: this.credentialsFormGroup.get(CredentialKey.tenantId)?.value || '',
        applicationId: this.inLineIntegrationFormGroup.controls.inLineServiceApplicationId.value,
      };

      if (this.inLineIntegrationId) {
        if (!this.inLineIntegrationFormGroup.dirty) {
          return;
        }

        this._updateInLineIntegration(this.inLineIntegrationId, updateData);
      } else {
        this._createInLineIntegration(relatedIntegrationId, updateData);
      }
    } catch (error) {
      this._onInLineWebhookSaveFailure(error);
    } finally {
      this.isLoading$.next(false);
    }
  }

  private _createInLineIntegration(relatedIntegrationId: string, updateData: InLineIntegrationUpdateData) {
    const createRequest: CreateEdgeIntegrationRequest = {
      integrationId: relatedIntegrationId,
      provider: 'copilotStudio',
      ...updateData,
    };

    this._inLineIntegrationsHttpService
      .createInLineIntegration(createRequest)
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: (response: InLineIntegration) => {
          this._onInLineWebhookSaveSuccess(response);
        },
        error: (error) => {
          this._onInLineWebhookSaveFailure(error);
        },
      });
  }

  public onRemoveInlineIntegration(): void {
    const integrationId = this.inLineIntegrationId;
    if (!integrationId) {
      this._loggerService.error("In-line webhook remove button was visible when it shouldn't be", {
        inLineIntegrationId: this.inLineIntegrationId,
        inLineIntegrationExpanded: this.inlineWebhookExpanded(),
      });
      return;
    }

    this._openRemoveConfirmationDialog(
      'integrationPage.removeCopilotStudioConfirmation.title',
      'integrationPage.removeCopilotStudioConfirmation.content'
    )
      .pipe(
        concatMap((confirmed: boolean) => {
          if (!confirmed) {
            return EMPTY;
          }
          return this._inLineIntegrationsHttpService.deleteInLineIntegration(integrationId, 'copilotStudio');
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._onInLineWebhookRemoveSuccess();
        },
        error: (error) => {
          this._onInLineWebhookRemoveFailure(error);
        },
      });
  }

  public onRemoveFoundryIntegration(): void {
    const integrationId = this.foundryIntegrationId;
    if (!integrationId) {
      this._loggerService.error("Foundry remove button was visible when it shouldn't be", {
        foundryIntegrationId: this.foundryIntegrationId,
        foundryEnabled: this.foundryEnabled(),
      });
      return;
    }

    this._openRemoveConfirmationDialog(
      'integrationPage.removeFoundryConfirmation.title',
      'integrationPage.removeFoundryConfirmation.content'
    )
      .pipe(
        concatMap((confirmed: boolean) => {
          if (!confirmed) {
            return EMPTY;
          }
          return this._inLineIntegrationsHttpService.deleteInLineIntegration(integrationId, 'foundry');
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._onFoundryWebhookRemoveSuccess();
        },
        error: (error) => {
          this._onFoundryWebhookRemoveFailure(error);
        },
      });
  }

  private _openRemoveConfirmationDialog(titleKey: string, contentKey: string): Observable<boolean> {
    const dialogRef = this.deleteConnectionDialog.open(ConfirmationDialogComponent, {
      data: {
        title: this._labelsProviderPipe.transform(titleKey),
        content: this._labelsProviderPipe.transform(contentKey),
      },
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    });
    return dialogRef.afterClosed();
  }

  private _updateInLineIntegration(inLineIntegrationId: string, updateData: InLineIntegrationUpdateData) {
    this._inLineIntegrationsHttpService
      .updateInLineIntegration(inLineIntegrationId, updateData)
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: (response: InLineIntegration) => {
          this._onInLineWebhookSaveSuccess(response);
        },
        error: (error) => {
          this._onInLineWebhookSaveFailure(error);
        },
      });
  }

  // Error is unknown because we want to handle any kind of error
  private _onInLineWebhookSaveFailure(error: unknown) {
    if (error instanceof HttpErrorResponse) {
      this._httpErrorHandlerService.handle(error);
    }
    this._notificationService.error('In-line webhook save failed');
    this.errorMessageInlineWebhook.next(
      this._labelsProviderPipe.transform('integrationPage.inLineWebhookService.errorMessageOnUpdateInlineIntegration')
    );
  }

  private _onInLineWebhookRemoveFailure(error: unknown) {
    if (error instanceof HttpErrorResponse) {
      this._httpErrorHandlerService.handle(error);
    }
    this._notificationService.error('In-line webhook remove failed');
    this.errorMessageInlineWebhook.next(
      this._labelsProviderPipe.transform('integrationPage.inLineWebhookService.errorMessageOnRemoveInlineIntegration')
    );
  }

  private _onInLineWebhookRemoveSuccess() {
    this.inLineIntegration = undefined;
    this.inLineIntegrationId = undefined;
    this.inLineIntegrationApplicationId = undefined;
    this.copilotStudioEnabled.set(false);
    this._mainToggleExpanded.set(false);
    this._setEmptyInLineIntegrationFormGroup();
    this._notificationService.success('In-line webhook removed successfully');
    this.inlineWebhookWasUpdated = true;
  }

  private _onInLineWebhookSaveSuccess(inLineIntegration: InLineIntegration) {
    this.inLineIntegration = inLineIntegration;
    this.inLineIntegrationId = inLineIntegration.id;
    this.inLineIntegrationApplicationId = inLineIntegration.applicationId;
    this.copilotStudioEnabled.set(true);
    this._setInlineIntegrationValues(inLineIntegration);
    this._notificationService.success('In-line webhook saved successfully');
    this.inlineWebhookWasUpdated = true;
  }

  private _onEditIntegrationAuthentication() {
    if (this.authMethodHasChanged) {
      this._editIntegrationAuthAndCredentials();
    } else {
      this._editIntegrationCredentials();
    }
  }

  private _initSinglePage(closeDialogOnSuccess = false) {
    if (this.integrationId) {
      this.isLoading$.next(true);
      combineLatest({
        integration: this._integrationsHttpService.getIntegration(this.integrationId),
        credentials: this._integrationsHttpService.getIntegrationCredentials(this.integrationId),
      })
        .pipe(finalize(() => this.isLoading$.next(false)))
        .subscribe({
          next: ({ integration, credentials }) => {
            this.integration = integration;
            this.integrationCredentials = credentials;

            if (closeDialogOnSuccess && integrationIsNotFailing(this.integration)) {
              this.closeWindow(true);
              return;
            }
            this._initIntegration();

            if (closeDialogOnSuccess) {
              return;
            }

            if (integration.platform === Platform.devices || integration.platform === Platform.cursor) {
              // Cursor reuses the devices infrastructure on ai-edge-manager
              this._devicesEnrollmentKeyHttpService.getEnrollmentKey(Platform.devices, integration.id).subscribe({
                next: (response) => {
                  this.devicesEnrollmentKey = response.key;
                },
                error: (error) => {
                  if (error.status !== 404) {
                    this._httpErrorHandlerService.handle(error);
                  }
                },
              });
            } else if (
              this.__shouldShowInlineIntegrationSection(integration.platform, integration.authenticationMethod)
            ) {
              this._getInLineIntegration(integration.id).subscribe({
                next: (inLineIntegration) => {
                  this.inLineIntegration = inLineIntegration;
                  this._initInLineIntegration();
                },
                error: (error) => {
                  if (error.status !== 404) {
                    this._httpErrorHandlerService.handle(error);
                  }
                  this._initInLineIntegration();
                },
              });
              this._initFoundrySection();
              this._loadFoundryIntegration(integration.id);
              this._checkFoundryApiKeyExists();
            }
          },
          error: (error) => {
            this._httpErrorHandlerService.handle(error);
            if (error.status === 404) {
              this.showNoData = true;
            }
          },
        });
    } else {
      this._initIntegration();
      this._initInLineIntegration();
      this._initFoundrySection();
      this.isLoading$.next(false);
    }
  }

  private _initInLineIntegration() {
    if (this.inLineIntegration) {
      this._setInlineIntegrationValues(this.inLineIntegration);
    } else {
      this._setEmptyInLineIntegrationFormGroup();
    }

    this.copilotStudioEnabled.set(!!this.inLineIntegrationId);
  }

  private _setEmptyInLineIntegrationFormGroup() {
    this.inLineIntegrationFormGroup = this._fb.group({
      inLineServiceEnabled: new UntypedFormControl(false),
      inLineServiceApplicationId: new UntypedFormControl(this.inLineIntegration?.applicationId ?? '', [
        ...GuidCredentialValidatorsFunctions,
        Validators.required,
      ]),
    });
  }

  private _setInlineIntegrationValues(inLineIntegration: InLineIntegration): void {
    this.inLineIntegrationApplicationId = inLineIntegration.applicationId;
    this.inLineIntegrationId = inLineIntegration.id;

    this.inLineIntegrationFormGroup = this._fb.group({
      inLineServiceEnabled: new UntypedFormControl(true),
      inLineServiceApplicationId: new UntypedFormControl(
        this.inLineIntegrationApplicationId,
        this._inLineApplicationIdValidators
      ),
    });
  }
  private _getInLineIntegration(integrationId: string): Observable<InLineIntegration | undefined> {
    if (this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.InlinePrevention)) {
      return this._inLineIntegrationsHttpService
        .getInLineIntegrationByRelatedIntegrationId(integrationId, 'copilotStudio')
        .pipe(
          catchError((error) => {
            if (error.status !== 404) {
              this._httpErrorHandlerService.handle(error);
            }
            return of(undefined);
          })
        );
    }
    return of(undefined);
  }

  private _initIntegration() {
    if (integrationIsFailing(this.integration) && this.integration.connectionStatus?.errorDisplayMessage) {
      this.errorMessage.next(this.integration.connectionStatus.errorDisplayMessage);
    }

    this.integrationFormGroup = this._fb.group({
      name: new UntypedFormControl(this.integration.name ?? '', [Validators.required]),
      platform: new UntypedFormControl(this.integration.platform ?? '', [Validators.required]),
      environment: new UntypedFormControl(this.integration.environment ?? '', [Validators.required]),
      authMethod: new UntypedFormControl(this.integration.authenticationMethod ?? '', [Validators.required]),
      credentials: this.credentialsFormGroup,
    });

    this._initIntegrationNameInput();

    // Register for change of auth method
    this.integrationFormGroup.controls.authMethod.valueChanges.subscribe(() => {
      this.formSubmitAttempt = false;
      this.authMethodHasChanged = true;
      this.errorMessage.next('');
      this._updateInlineControlSectionVisibility();
    });

    // Register for change of platform (for create mode preview)
    this.integrationFormGroup.controls.platform.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._updateCreateModeInlinePreviewVisibility();
      });

    this._updateInlineControlSectionVisibility();
    this._updateCreateModeInlinePreviewVisibility();
  }

  private _initIntegrationNameInput() {
    // initial integration name and credentials to be compared on save
    this._integrationInitialName = this.integrationFormGroup.controls.name.value;

    this.integrationNameDetail = {
      key: this._requiredFieldPipe.transform('integrationPage.integrationNameField'),
      formValue: this.integrationFormGroup.controls.name,
    };
  }

  private _updateInlineControlSectionVisibility(): void {
    if (!this.isInlinePreventionEnabled || !this.integrationFormGroup) {
      this.shouldShowInlineControlSection.set(false);
      return;
    }

    const platform = this.integrationFormGroup.controls.platform.value;
    const authMethod = this.integrationFormGroup.controls.authMethod.value;

    const shouldShow = this.__shouldShowInlineIntegrationSection(platform, authMethod);

    this.shouldShowInlineControlSection.set(shouldShow);
  }

  private __isInlineWebhookEligiblePlatform(platform: string): boolean {
    return platform === Platform.powerplatform || platform === Platform.outpost_powerplatform;
  }

  private __shouldShowInlineIntegrationSection(platform: string, authMethod: string): boolean {
    return (
      this.isInlinePreventionEnabled &&
      this.integrationId !== undefined &&
      this.__isInlineWebhookEligiblePlatform(platform) &&
      !!authMethod &&
      authMethod !== AuthenticationMethod.oauthAuthorizationCode
    );
  }

  /**
   * This is needed when the user only changes the auth method
   * Because the user changed the auth method he needs to add all the credentials and that is the reason that we handle the credentials like the add integration.
   */
  private _editIntegrationAuthAndCredentials() {
    const credentials = this._getFilteredCredentials();
    this.isLoading$.next(true);
    this._integrationsHttpService
      .editIntegrationAuthentication(
        this.integration.id,
        this.integrationFormGroup.controls.authMethod.value as AuthenticationMethod,
        credentials
      )
      .subscribe({
        next: () => {
          this.integrationWasUpdated = true;
          this.shouldUpdateCredentials = false;
          this._initSinglePage(true);
        },
        error: (error) => {
          this.shouldUpdateCredentials = false;
          this.errorMessage.next(this._labelsProviderPipe.transform('integrationPage.errorMessageOnUpdateCredentials'));
          this._httpErrorHandlerService.handle(error);
          this.isLoading$.next(false);
        },
      });
  }

  /**
   * This is needed when the user only update a credential from the same auth method.
   * On this specific case the UI hasn't all the credentials because the server doesn't return the secrets.
   */
  private _editIntegrationCredentials() {
    const credentials = this._getFilteredCredentials();
    const splittedCredentials = this._splitCredentialsToSecretAndNonSecret(credentials);
    this._integrationsHttpService
      .editIntegrationCredentials(
        this.integration.id,
        splittedCredentials.nonSecretCredentials,
        splittedCredentials.secretCredentials
      )
      .subscribe({
        next: () => {
          this.integrationWasUpdated = true;
          this._initSinglePage(true);
        },
        error: (error) => {
          this.isLoading$.next(false);
          this.errorMessage.next(this._labelsProviderPipe.transform('integrationPage.errorMessageOnUpdateCredentials'));
          this._httpErrorHandlerService.handle(error);
        },
      });
  }

  private _shouldUpdateCredentials() {
    let shouldUpdateCredentials =
      JSON.stringify(this.integrationCredentials) !== JSON.stringify(this.credentialsFormGroup.value);

    if (this.integrationFormGroup.controls.authMethod.value === AuthenticationMethod.oauthAuthorizationCode) {
      // in edit mode - auth code is not part of the credentials control since its not returned in api
      shouldUpdateCredentials =
        shouldUpdateCredentials && this.credentialsFormGroup.get(CredentialKey.authCode) !== null;

      if (shouldUpdateCredentials) {
        // add oauth control with validators
        const control = new FormControl<string>(
          this.integrationCredentials[CredentialKey.authCode] ?? '',
          RequiredValidatorFunction.concat(CredentialValidatorsFunctions)
        );
        this.credentialsFormGroup.addControl(CredentialKey.authCode, control);
      }
    }

    return shouldUpdateCredentials;
  }

  private _getFilteredCredentials(): IntegrationCredentials {
    const formValue = this.credentialsFormGroup.value;
    const currentPlatform = this.integrationFormGroup.controls.platform.value as Platform;
    const currentAuthMethod = this.integrationFormGroup.controls.authMethod.value as AuthenticationMethod;

    if (currentPlatform === Platform.google && currentAuthMethod === AuthenticationMethod.serviceAccountManaged) {
      const organizationId = formValue[CredentialKey.organizationId];
      return organizationId ? { organization_id: organizationId } : {};
    }

    // For Budibase platform, filter out empty values to support mutual exclusion validation
    if (currentPlatform === Platform.budibase) {
      const credentials: IntegrationCredentials = {};

      for (const key in formValue) {
        const value = formValue[key];
        // Only include fields that have actual values (not empty strings, null, or undefined)
        if (value && value.trim && value.trim() !== '') {
          credentials[key] = value;
        } else if (value && typeof value !== 'string') {
          // Handle non-string values that are truthy
          credentials[key] = value;
        }
      }

      return credentials;
    }

    // For all other platforms, return the original form value without filtering
    return formValue;
  }

  private _splitCredentialsToSecretAndNonSecret(allCredentials: IntegrationCredentials): {
    secretCredentials: IntegrationCredentials;
    nonSecretCredentials: IntegrationCredentials;
  } {
    const secretCredentials: IntegrationCredentials = {};
    const nonSecretCredentials: IntegrationCredentials = {};

    for (const key in allCredentials) {
      if (allCredentials[key]) {
        if (SecretCredentialsKeys.includes(<CredentialKey>key)) {
          secretCredentials[key] = allCredentials[key];
        } else {
          nonSecretCredentials[key] = allCredentials[key];
        }
      }
    }

    return { secretCredentials, nonSecretCredentials };
  }

  private _createInlineIntegrationForEnrollmentKeyPlatform(
    integrationId: string,
    platform: Platform
  ): Observable<InLineIntegration | null> {
    return this._inLineIntegrationsHttpService
      .createInLineIntegration({
        integrationId: integrationId,
        provider: platform as unknown as Provider,
        platformAccountId: uuidv4(),
        applicationId: uuidv4(),
      })
      .pipe(
        catchError((error) => {
          this._httpErrorHandlerService.handle(error);
          return of(null);
        })
      );
  }

  private _createEnrollmentKeyForEnrollmentKeyPlatform(platform: string, integrationId: string): Observable<EnrollmentKeyResponse | null> {
    return this._devicesEnrollmentKeyHttpService.createEnrollmentKey(platform, integrationId).pipe(
      tap((response: EnrollmentKeyResponse) => {
        this.devicesEnrollmentKey = response.key;
        this._notificationService.success(
          `'${this.integrationFormGroup.controls.name.value}' integration was created.`
        );
      }),
      catchError((error) => {
        this._httpErrorHandlerService.handle(error);
        return of(null);
      })
    );
  }

  // Foundry section methods
  public onSaveFoundryIntegration(): void {
    const tenantId = this.credentialsFormGroup.get(CredentialKey.tenantId)?.value || '';
    const relatedIntegrationId = this.integrationId;

    if (!relatedIntegrationId || !this.foundryFormGroup.valid || !this.foundryEnabled() || !tenantId) {
      this._loggerService.error("Foundry save button was visible/enabled when it shouldn't be", {
        integrationId: relatedIntegrationId,
        isFoundryFormGroupValid: this.foundryFormGroup.valid,
        isFoundryEnabled: this.foundryEnabled(),
        tenantId: tenantId,
      });
      return;
    }

    this.isLoading$.next(true);

    const updateData: InLineIntegrationUpdateData = {
      platformAccountId: tenantId,
      applicationId: this.foundryIntegration?.applicationId || uuidv4(),
    };

    if (this.foundryIntegrationId) {
      this._updateFoundryIntegration(this.foundryIntegrationId, updateData);
    } else {
      this._createFoundryIntegration(relatedIntegrationId, updateData);
    }
  }

  private _createFoundryIntegration(relatedIntegrationId: string, updateData: InLineIntegrationUpdateData): void {
    const createRequest: CreateEdgeIntegrationRequest = {
      integrationId: relatedIntegrationId,
      provider: 'foundry',
      ...updateData,
    };

    this._inLineIntegrationsHttpService
      .createInLineIntegration(createRequest)
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: (response: InLineIntegration) => {
          this._onFoundrySaveSuccess(response);
        },
        error: (error) => {
          this._onFoundrySaveFailure(error);
        },
      });
  }

  private _updateFoundryIntegration(foundryIntegrationId: string, updateData: InLineIntegrationUpdateData): void {
    this._inLineIntegrationsHttpService
      .updateInLineIntegration(foundryIntegrationId, updateData)
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: (response: InLineIntegration) => {
          this._onFoundrySaveSuccess(response);
        },
        error: (error) => {
          this._onFoundrySaveFailure(error);
        },
      });
  }

  private _onFoundrySaveSuccess(foundryIntegration: InLineIntegration): void {
    this.foundryIntegration = foundryIntegration;
    this.foundryIntegrationId = foundryIntegration.id;
    this.foundryApplicationId.set(foundryIntegration.applicationId);
    this._notificationService.success(
      this._labelsProviderPipe.transform('integrationPage.foundryService.webhookSavedMessage')
    );
    this.inlineWebhookWasUpdated = true;
  }

  private _onFoundryWebhookRemoveSuccess(): void {
    this.foundryIntegration = undefined;
    this.foundryIntegrationId = undefined;
    this.foundryApplicationId.set('');
    this.foundryApiKeyExists.set(false);
    this.foundryApiKeyVisible.set(false);
    this.foundryApiKeyValue.set('');
    this._mainToggleExpanded.set(false);
    this._notificationService.success('In-line webhook removed successfully');
    this.inlineWebhookWasUpdated = true;
  }

  private _onFoundryWebhookRemoveFailure(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      this._httpErrorHandlerService.handle(error);
    }
    this._notificationService.error('Failed to remove in-line webhook');
  }

  private _onFoundrySaveFailure(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      this._httpErrorHandlerService.handle(error);
    }
    this._notificationService.error(
      this._labelsProviderPipe.transform('integrationPage.foundryService.errorGeneratingApiKey')
    );
    this.foundryEnabled.set(false);
    this.foundryFormGroup?.controls?.foundryEnabled?.setValue(false, { emitEvent: false });
  }

  public onGenerateFoundryApiKey(): void {
    if (this.foundryApiKeyExists()) {
      return;
    }

    const accountId = this._userService.zenityAccountId;
    const userEmail = this._userService.loggedInUser?.email || 'unknown';

    this.isLoading$.next(true);

    this._inLineIntegrationsHttpService
      .generateFoundryApiKey({
        zenityAccountId: accountId,
        integrationId: this.integrationId,
        inlineIntegrationId: this.foundryIntegrationId,
        createdBy: userEmail,
      })
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: (response: FoundryApiKeyResponse) => {
          this.foundryApiKeyValue.set(response.apiKey);
          this.foundryApiKeyExists.set(true);
          this.foundryApiKeyVisible.set(true);
          this._notificationService.success(
            this._labelsProviderPipe.transform('integrationPage.foundryService.apiKeyGeneratedMessage')
          );
          this.inlineWebhookWasUpdated = true;
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            this._httpErrorHandlerService.handle(error);
          }
          this._notificationService.error(
            this._labelsProviderPipe.transform('integrationPage.foundryService.errorGeneratingApiKey')
          );
        },
      });
  }

  public onRevokeFoundryApiKey(): void {
    if (!this.foundryApiKeyExists()) {
      return;
    }

    const accountId = this._userService.zenityAccountId;
    const integrationId = this.integrationId;

    if (!integrationId) {
      return;
    }

    this.isLoading$.next(true);

    this._inLineIntegrationsHttpService
      .revokeFoundryApiKey(accountId, integrationId)
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: () => {
          this.foundryApiKeyValue.set('');
          this.foundryApiKeyExists.set(false);
          this.foundryApiKeyVisible.set(false);
          this._notificationService.success(
            this._labelsProviderPipe.transform('integrationPage.foundryService.apiKeyRevokedMessage')
          );
          this.inlineWebhookWasUpdated = true;
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            this._httpErrorHandlerService.handle(error);
          }
          this._notificationService.error(
            this._labelsProviderPipe.transform('integrationPage.foundryService.errorRevokingApiKey')
          );
        },
      });
  }

  public async onCopyFoundryApiKey(): Promise<void> {
    if (!this.foundryApiKeyVisible() || !this.foundryApiKeyValue()) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.foundryApiKeyValue());
      this.foundryApiKeyCopied.set(true);
      this._notificationService.success(
        this._labelsProviderPipe.transform('integrationPage.foundryService.apiKeyCopiedMessage')
      );
      setTimeout(() => this.foundryApiKeyCopied.set(false), 2000);
    } catch {
      this._notificationService.error('Failed to copy API key');
    }
  }

  public getDisplayApiKey(): string {
    if (!this.foundryApiKeyExists()) {
      return '';
    }
    return this.foundryApiKeyVisible() ? this.foundryApiKeyValue() : '*************************';
  }

  private _initFoundrySection(): void {
    this.foundryFormGroup = this._fb.group({
      foundryEnabled: new UntypedFormControl(false),
    });

    this.foundryEnabled.set(false);
  }

  private _loadFoundryIntegration(integrationId: string): void {
    this._inLineIntegrationsHttpService
      .getInLineIntegrationByRelatedIntegrationId(integrationId, 'foundry')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (foundryIntegration) => {
          this.foundryIntegration = foundryIntegration;
          this.foundryIntegrationId = foundryIntegration.id;
          this.foundryApplicationId.set(foundryIntegration.applicationId);
          this.foundryEnabled.set(true);
          this.foundryFormGroup?.controls?.foundryEnabled?.setValue(true, { emitEvent: false });
        },
        error: (error) => {
          if (error.status !== 404) {
            this._httpErrorHandlerService.handle(error);
          }
          this.foundryIntegration = undefined;
          this.foundryIntegrationId = undefined;
          this.foundryEnabled.set(false);
        },
      });
  }

  private _checkFoundryApiKeyExists(): void {
    const accountId = this._userService.zenityAccountId;
    const integrationId = this.integrationId;

    if (!integrationId) {
      return;
    }

    this._inLineIntegrationsHttpService.checkFoundryApiKeyExists(accountId, integrationId).subscribe({
      next: (response) => {
        this.foundryApiKeyExists.set(response.hasActiveKey);
        this.foundryApiKeyVisible.set(false);
      },
      error: () => {
        this.foundryApiKeyExists.set(false);
      },
    });
  }

  private _updateCreateModeInlinePreviewVisibility(): void {
    if (!this.isInlinePreventionEnabled || !this.integrationFormGroup) {
      this.shouldShowCreateModeInlinePreview.set(false);
      return;
    }

    const platform = this.integrationFormGroup.controls.platform.value;
    const isCreateMode = this.integrationId === undefined;

    this.shouldShowCreateModeInlinePreview.set(isCreateMode && this.__isInlineWebhookEligiblePlatform(platform));
  }
}
